import React, { Component, PropTypes } from "react";
import TextField from "material-ui/TextField";
import DropDownMenu from "material-ui/DropDownMenu";

function getStyles(props, { muiTheme = {} }) {
  const { selectField = {} } = muiTheme;
  const {
    fontFamily,
    fontWeight,
    fontSize,
    largeFontSize,
    borderColor,
    iconFill,
    iconBackgroundColor,
    hintColor
  } = selectField;

  return {
    root: {
      height: props.large ? 44 : 32
    },
    listStyle: {
      paddingTop: 0,
      paddingBottom: 0
    },
    menuItemStyle: {
      padding: props.large ? "8px 0" : "0px",
      fontWeight: fontWeight,
      fontFamily,
      fontSize: props.large ? largeFontSize : fontSize
    },
    label: {
      textAlign: "left",
      paddingLeft: props.large ? 15 : 5,
      paddingRight: props.large ? 56 : 37,
      fontWeight: fontWeight,
      fontFamily,
      fontSize: props.large ? largeFontSize : fontSize,
      top: props.floatingLabelText ? 6 : 0,
      height: props.large ? 44 : 32,
      lineHeight: props.large ? "44px" : "30px"
    },
    icon: {
      backgroundColor: iconBackgroundColor || "midnightblue",
      borderRadius: "0 5px 5px 0",
      margin: "-1px",
      right: 0,
      top: props.floatingLabelText ? 8 : 0,
      fill: iconFill || "white",
      height: props.large ? 44 : 32,
      width: props.large ? 44 : 32,
      padding: props.large ? 10 : 4
    },
    hideDropDownUnderline: {
      borderTop: "none"
    },
    dropDownMenu: {
      display: "block",
      border: "1px solid",
      borderColor: borderColor || "lightgrey",
      borderRadius: "5px",
      height: props.large ? 44 : 32
    },
    hint: {
      left: props.large ? 15 : 5,
      bottom: props.large ? 10 : 3,
      fontFamily,
      fontWeight: fontWeight,
      fontSize: props.large ? largeFontSize : fontSize,
      color: hintColor
    }
  };
}

class SelectField extends Component {
  static propTypes = {
    /**
     * If true, the width will automatically be set according to the
     * items inside the menu.
     * To control the width in CSS instead, leave this prop set to `false`.
     */
    autoWidth: PropTypes.bool,
    /**
     * The `MenuItem` elements to populate the select field with.
     * If the menu items have a `label` prop, that value will
     * represent the selected menu item in the rendered select field.
     */
    children: PropTypes.node,
    /**
     * If true, the select field will be disabled.
     */
    disabled: PropTypes.bool,
    /**
     * Override the inline-styles of the error element.
     */
    errorStyle: PropTypes.object,
    /**
     * The error content to display.
     */
    errorText: PropTypes.node,
    /**
     * If true, the floating label will float even when no value is selected.
     */
    floatingLabelFixed: PropTypes.bool,
    /**
     * Override the inline-styles of the floating label.
     */
    floatingLabelStyle: PropTypes.object,
    /**
     * The content of the floating label.
     */
    floatingLabelText: PropTypes.node,
    /**
     * If true, the select field will take up the full width of its container.
     */
    fullWidth: PropTypes.bool,
    /**
     * Override the inline-styles of the hint element.
     */
    hintStyle: PropTypes.object,
    /**
     * The hint content to display.
     */
    hintText: PropTypes.node,
    /**
     * Override the inline-styles of the icon element.
     */
    iconStyle: PropTypes.object,
    /**
     * The id prop for the text field.
     */
    id: PropTypes.string,
    /**
     * Override the label style when the select field is inactive.
     */
    labelStyle: PropTypes.object,
    /**
     * Override the inline-styles of the underlying `List` element.
     */
    listStyle: PropTypes.object,
    /**
     * Override the default max-height of the underlying `DropDownMenu` element.
     */
    maxHeight: PropTypes.number,
    /**
     * Override the inline-styles of menu items.
     */
    menuItemStyle: PropTypes.object,
    /**
     * Override the inline-styles of the underlying `DropDownMenu` element.
     */
    menuStyle: PropTypes.object,
    /**
     * If true, `value` must be an array and the menu will support
     * multiple selections.
     */
    multiple: PropTypes.bool,
    /** @ignore */
    onBlur: PropTypes.func,
    /**
     * Callback function fired when a menu item is selected.
     *
     * @param {object} event TouchTap event targeting the menu item
     * that was selected.
     * @param {number} key The index of the selected menu item, or undefined
     * if `multiple` is true.
     * @param {any} payload If `multiple` is true, the menu's `value`
     * array with either the menu item's `value` added (if
     * it wasn't already selected) or omitted (if it was already selected).
     * Otherwise, the `value` of the menu item.
     */
    onChange: PropTypes.func,
    /** @ignore */
    onFocus: PropTypes.func,
    /**
     * Override the inline-styles of selected menu items.
     */
    selectedMenuItemStyle: PropTypes.object,
    /**
     * Customize the rendering of the selected item.
     *
     * @param {any} value If `multiple` is true, the menu's `value`
     * array with either the menu item's `value` added (if
     * it wasn't already selected) or omitted (if it was already selected).
     * Otherwise, the `value` of the menu item.
     */
    selectionRenderer: PropTypes.func,
    /**
     * Override the inline-styles of the root element.
     */
    style: PropTypes.object,
    /**
     * Override the inline-styles of the underline element when the select
     * field is disabled.
     */
    underlineDisabledStyle: PropTypes.object,
    /**
     * Override the inline-styles of the underline element when the select field
     * is focused.
     */
    underlineFocusStyle: PropTypes.object,
    /**
     * Override the inline-styles of the underline element.
     */
    underlineStyle: PropTypes.object,
    /**
     * If `multiple` is true, an array of the `value`s of the selected
     * menu items. Otherwise, the `value` of the selected menu item.
     * If provided, the menu will be a controlled component.
     */
    value: PropTypes.any
  };

  static defaultProps = {
    autoWidth: false,
    disabled: false,
    fullWidth: false,
    multiple: false
  };

  static contextTypes = {
    muiTheme: PropTypes.object.isRequired
  };

  render() {
    const {
      autoWidth,
      multiple,
      children,
      style,
      labelStyle,
      iconStyle,
      id,
      underlineDisabledStyle,
      underlineFocusStyle,
      menuItemStyle,
      selectedMenuItemStyle,
      underlineStyle,
      errorStyle,
      disabled,
      floatingLabelFixed,
      floatingLabelText,
      floatingLabelStyle,
      hintStyle,
      hintText,
      fullWidth,
      errorText,
      listStyle,
      maxHeight,
      menuStyle,
      onFocus,
      onBlur,
      onChange,
      selectionRenderer,
      value,
      large,
      ...other
    } = this.props;

    const styles = getStyles(this.props, this.context);

    const wrappedChildren = React.Children.map(children, child =>
      React.cloneElement(child, {
        innerDivStyle: {
          padding: large ? "0 15px" : "0 5px",
          ...(child.props.innerDivStyle || {})
        }
      })
    );

    return (
      <TextField
        {...other}
        style={Object.assign(styles.root, style)}
        disabled={disabled}
        floatingLabelFixed={floatingLabelFixed}
        floatingLabelText={floatingLabelText}
        floatingLabelStyle={floatingLabelStyle}
        hintStyle={Object.assign(styles.hint, hintStyle)}
        hintText={!hintText && !floatingLabelText ? " " : hintText}
        fullWidth={fullWidth}
        errorText={errorText}
        underlineStyle={underlineStyle}
        errorStyle={errorStyle}
        onFocus={onFocus}
        onBlur={onBlur}
        id={id}
        underlineDisabledStyle={underlineDisabledStyle}
        underlineFocusStyle={underlineFocusStyle}
        underlineShow={false}
      >
        <DropDownMenu
          disabled={disabled}
          style={Object.assign(styles.dropDownMenu, menuStyle)}
          labelStyle={Object.assign(styles.label, labelStyle)}
          iconStyle={Object.assign(styles.icon, iconStyle)}
          menuItemStyle={Object.assign(styles.menuItemStyle, menuItemStyle)}
          selectedMenuItemStyle={selectedMenuItemStyle}
          underlineStyle={styles.hideDropDownUnderline}
          listStyle={Object.assign(styles.listStyle, listStyle)}
          autoWidth={autoWidth}
          value={value}
          onChange={onChange}
          maxHeight={maxHeight}
          multiple={multiple}
          selectionRenderer={selectionRenderer}
        >
          {wrappedChildren}
        </DropDownMenu>
      </TextField>
    );
  }
}

export default SelectField;



// WEBPACK FOOTER //
// ./src/common/muiComponents/SelectField.js